import { Input } from '@/components/ui/input';

import { VariableAutocomplete } from '../VariableAutocomplete';

import type { WorkflowNode } from '@repo/shared-types';

interface GetQualityIssuesConfigProps {
  node: WorkflowNode;
  onDataChange: (nodeId: string, data: Record<string, unknown>) => void;
}

const SEVERITY_OPTIONS = [
  { value: 'major', label: 'Major + Critical' },
  { value: 'critical', label: 'Critical only' },
  { value: 'minor', label: 'All (minor and up)' },
];

const DETAIL_OPTIONS = [
  { value: 'summary', label: 'Summary (failure_summary)' },
  { value: 'evidence', label: 'Evidence (verbatim quote)' },
  { value: 'reasoning', label: 'Reasoning (full verdict)' },
];

export const GetQualityIssuesConfig = ({ node, onDataChange }: GetQualityIssuesConfigProps) => {
  const data = node.data;

  const update = (field: string, value: unknown) => {
    onDataChange(node.id, { [field]: value });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-muted-foreground text-xs font-medium">Label</label>
        <Input
          value={(data.label as string) ?? 'Quality Issues'}
          onChange={(e) => update('label', e.target.value)}
          className="mt-1"
        />
      </div>

      <div>
        <label className="text-muted-foreground text-xs font-medium">Date From</label>
        <VariableAutocomplete
          value={(data.dateFrom as string) ?? '{{date_offset:-1}}'}
          onChange={(v) => update('dateFrom', v)}
          placeholder="e.g. {{date_offset:-1}} (yesterday)"
          className="mt-1"
          inputClassName="font-mono"
        />
        <p className="text-muted-foreground mt-1 text-[10px]">
          {
            '{{date_offset:-1}} = yesterday, {{date_offset:0}} = today, {{date_offset:-7}} = 7 days ago'
          }
        </p>
      </div>

      <div>
        <label className="text-muted-foreground text-xs font-medium">Date To</label>
        <VariableAutocomplete
          value={(data.dateTo as string) ?? '{{date_offset:-1}}'}
          onChange={(v) => update('dateTo', v)}
          placeholder="e.g. {{date_offset:-1}}"
          className="mt-1"
          inputClassName="font-mono"
        />
      </div>

      <div>
        <label className="text-muted-foreground text-xs font-medium">Minimum Severity</label>
        <select
          value={(data.minSeverity as string) ?? 'major'}
          onChange={(e) => update('minSeverity', e.target.value)}
          className="border-input bg-background focus-visible:ring-ring mt-1 w-full rounded-md border px-3 py-2 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none"
        >
          {SEVERITY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <p className="text-muted-foreground mt-1 text-[10px]">
          Which flagged calls to include. Cosmetic/minor issues are excluded at &quot;Major +
          Critical&quot;.
        </p>
      </div>

      <div>
        <label className="text-muted-foreground text-xs font-medium">Detail column</label>
        <select
          value={(data.detailField as string) ?? 'summary'}
          onChange={(e) => update('detailField', e.target.value)}
          className="border-input bg-background focus-visible:ring-ring mt-1 w-full rounded-md border px-3 py-2 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none"
        >
          {DETAIL_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <p className="text-muted-foreground mt-1 text-[10px]">
          Which text to show in the last table column of the email.
        </p>
      </div>

      <div>
        <label className="text-muted-foreground text-xs font-medium">Output Key</label>
        <Input
          value={(data.outputKey as string) ?? 'quality_issues'}
          onChange={(e) => update('outputKey', e.target.value)}
          className="mt-1 font-mono"
          placeholder="quality_issues"
        />
        <p className="text-muted-foreground mt-1 text-[10px]">
          {
            'Use {{quality_issues.html}} (the table) and {{quality_issues.count}} in a Send Email node.'
          }
        </p>
      </div>

      <div>
        <label className="text-muted-foreground text-xs font-medium">
          Call link base URL (optional)
        </label>
        <Input
          value={(data.callBaseUrl as string) ?? ''}
          onChange={(e) => update('callBaseUrl', e.target.value)}
          className="mt-1 font-mono"
          placeholder="https://your-app-url (defaults to FRONTEND_URL)"
        />
        <p className="text-muted-foreground mt-1 text-[10px]">
          {'Base URL for the per-call "View call" buttons. Links point to /calls/<id>.'}
        </p>
      </div>
    </div>
  );
};
